import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { DatabaseService } from '../lib/database';
import { supabase } from '../lib/supabase';
import type { Proforma } from '../types/database';
import { useDeal } from '../contexts/DealContext';
import { AlertCircle } from 'lucide-react';

type ExpenseRow = {
  id: string;
  name: string;
  before: string;
  after: string;
};

const currency = (value: string | number) => {
  if (value === null || value === undefined) return '$0';
  const str = typeof value === 'string' ? value : value.toString();
  const num = Number(str.replace(/[^0-9.-]/g, ''));
  if (Number.isNaN(num)) return '$0';
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

export const ProformaPage: React.FC = () => {
  const { dealId, scenarioId } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const { selectedDeal, selectedScenarioId, selectScenario } = useDeal();
  // Ensure context scenario syncs with route if present
  useEffect(() => {
    if (scenarioId && scenarioId !== selectedScenarioId) {
      selectScenario(scenarioId);
    }
  }, [scenarioId, selectedScenarioId, selectScenario]);

  // Narrative text areas
  const [sales_narrative, setSalesNarrative] = useState('');
  const [cogs_narrative, setCogsNarrative] = useState('');
  const [expenses_narrative, setExpensesNarrative] = useState('');
  const [refiningSales, setRefiningSales] = useState(false);
  const [refiningCogs, setRefiningCogs] = useState(false);
  const [refiningExpenses, setRefiningExpenses] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  
  // Force preview mode for read-only users
  useEffect(() => {
    if (selectedDeal?.access_type === 'read') {
      setIsPreviewMode(true);
    }
  }, [selectedDeal?.access_type]);

  // Baseline year selection
  const [baseline_year, setBaselineYear] = useState('2024');

  // Sales before/after
  const [sales_before, setSalesBefore] = useState('');
  const [sales_after, setSalesAfter] = useState('');

  // COGS before/after
  const [cogs_before, setCogsBefore] = useState('');
  const [cogs_after, setCogsAfter] = useState('');

  // Expenses before/after (total)
  const [expenses_before, setExpensesBefore] = useState('');
  const [expenses_after, setExpensesAfter] = useState('');

  // Expenses dynamic rows
  const [expense_categories, setExpenseRows] = useState<ExpenseRow[]>([
    { id: crypto.randomUUID(), name: 'Payroll', before: '', after: '' },
    { id: crypto.randomUUID(), name: 'Rent', before: '', after: '' },
    { id: crypto.randomUUID(), name: 'Marketing', before: '', after: '' },
  ]);

  // Per-year plans (Years 1-5)
  const [yearly_sales_after, setYearlySalesAfter] = useState<string[]>(['', '', '', '', '']);
  const [yearly_sales_narratives, setYearlySalesNarratives] = useState<string[]>(['', '', '', '', '']);
  const [yearly_cogs_after, setYearlyCogsAfter] = useState<string[]>(['', '', '', '', '']);
  const [yearly_cogs_narratives, setYearlyCogsNarratives] = useState<string[]>(['', '', '', '', '']);
  const [yearly_expenses_after, setYearlyExpensesAfter] = useState<string[]>(['', '', '', '', '']);
  const [yearly_expenses_narratives, setYearlyExpensesNarratives] = useState<string[]>(['', '', '', '', '']);

  // Save functionality state
  const [currentProforma, setCurrentProforma] = useState<Proforma | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const netChange = (before: string, after: string) => {
    const b = Number(before.replace(/[^0-9.-]/g, '')) || 0;
    const a = Number(after.replace(/[^0-9.-]/g, '')) || 0;
    const change = a - b;
    const pct = b !== 0 ? (change / b) * 100 : 0;
    return { change, pct };
  };

  // Load existing proforma data
  useEffect(() => {
    const loadProforma = async () => {
      if (!dealId || !user?.id) return;

      try {
        const response = await DatabaseService.getProformaByDealId(dealId, user.id);
        if (response.data) {
          setCurrentProforma(response.data);
          // Load data into form fields
          setSalesNarrative(response.data.sales_narrative || '');
          setSalesBefore(response.data.sales_before?.toString() || '');
          setSalesAfter(response.data.sales_after?.toString() || '');
          setCogsNarrative(response.data.cogs_narrative || '');
          setCogsBefore(response.data.cogs_before?.toString() || '');
          setCogsAfter(response.data.cogs_after?.toString() || '');
          setExpensesNarrative(response.data.expenses_narrative || '');
          setExpensesBefore(response.data.expenses_before?.toString() || '');
          setExpensesAfter(response.data.expenses_after?.toString() || '');
          
          // Load expense categories if they exist
          if (response.data.expense_categories && Array.isArray(response.data.expense_categories)) {
            setExpenseRows(response.data.expense_categories);
          }
          
          // Load metadata fields
          if (response.data.metadata) {
            setBaselineYear(response.data.metadata.baseline_year || '2024');
            setYearlySalesAfter(response.data.metadata.yearly_sales_after || ['', '', '', '', '']);
            setYearlySalesNarratives(response.data.metadata.yearly_sales_narratives || ['', '', '', '', '']);
            setYearlyCogsAfter(response.data.metadata.yearly_cogs_after || ['', '', '', '', '']);
            setYearlyCogsNarratives(response.data.metadata.yearly_cogs_narratives || ['', '', '', '', '']);
            setYearlyExpensesAfter(response.data.metadata.yearly_expenses_after || ['', '', '', '', '']);
            setYearlyExpensesNarratives(response.data.metadata.yearly_expenses_narratives || ['', '', '', '', '']);
          }
        }
      } catch (error) {
        console.error('Error loading proforma:', error);
      }
    };

    loadProforma();
  }, [dealId, user?.id]);

  // Save proforma data
  const handleSave = async () => {
    if (!dealId || !user?.id) {
      setSaveError('User not authenticated or deal ID missing');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveMessage(null);

    try {
      // Get the first deal scenario for this deal, create one if none exists
      let { data: scenarios } = await supabase
        .from('deal_scenarios')
        .select('id')
        .eq('deal_id', dealId)
        .limit(1)
        .maybeSingle();

      let scenarioId = scenarios?.id;

      // If no scenario exists, create a default one
      if (!scenarioId) {
        const { data: newScenario, error: scenarioError } = await supabase
          .from('deal_scenarios')
          .insert({
            deal_id: dealId,
            name: 'Default Scenario',
            business_name: 'Business Acquisition',
            annual_sde: 300000,
            monthly_sde: 25000
          })
          .select('id')
          .single();

        if (scenarioError) {
          console.error('Error creating default scenario:', scenarioError);
          setSaveError('Failed to create deal scenario');
          return;
        }

        scenarioId = newScenario.id;
      }

      const proformaData = {
        deal_id: dealId,
        deal_scenario_id: scenarioId,
        title: 'Business Acquisition Proforma',
        sales_narrative: sales_narrative,
        sales_before: Number(sales_before.replace(/[^0-9.-]/g, '')) || 0,
        sales_after: Number(sales_after.replace(/[^0-9.-]/g, '')) || 0,
        cogs_narrative: cogs_narrative,
        cogs_before: Number(cogs_before.replace(/[^0-9.-]/g, '')) || 0,
        cogs_after: Number(cogs_after.replace(/[^0-9.-]/g, '')) || 0,
        expenses_narrative: expenses_narrative,
        expenses_before: Number(expenses_before.replace(/[^0-9.-]/g, '')) || 0,
        expenses_after: Number(expenses_after.replace(/[^0-9.-]/g, '')) || 0,
        expense_categories: expense_categories,
        timeline_items: [], // TODO: Implement timeline items
        projections: {}, // TODO: Implement projections
        growth_assumptions: {}, // TODO: Implement growth assumptions
        metadata: {
          baseline_year: baseline_year,
          yearly_sales_after: yearly_sales_after,
          yearly_sales_narratives: yearly_sales_narratives,
          yearly_cogs_after: yearly_cogs_after,
          yearly_cogs_narratives: yearly_cogs_narratives,
          yearly_expenses_after: yearly_expenses_after,
          yearly_expenses_narratives: yearly_expenses_narratives
        }
      };

      let response;
      if (currentProforma) {
        // Update existing proforma
        response = await DatabaseService.updateProforma({
          id: currentProforma.id,
          ...proformaData
        }, user.id);
      } else {
        // Create new proforma
        response = await DatabaseService.createProforma(proformaData, user.id);
      }

      if (response.error) {
        setSaveError(response.error);
      } else {
        setSaveMessage('Proforma saved successfully!');
        setCurrentProforma(response.data || null);
        setTimeout(() => setSaveMessage(null), 3000);
      }
    } catch (error) {
      console.error('Error saving proforma:', error);
      setSaveError('Failed to save proforma');
    } finally {
      setIsSaving(false);
    }
  };

  const cogsPctOfSales = useMemo(() => {
    const sB = Number(sales_before.replace(/[^0-9.-]/g, '')) || 0;
    const sA = Number(sales_after.replace(/[^0-9.-]/g, '')) || 0;
    const cB = Number(cogs_before.replace(/[^0-9.-]/g, '')) || 0;
    const cA = Number(cogs_after.replace(/[^0-9.-]/g, '')) || 0;
    return {
      before: sB ? (cB / sB) * 100 : 0,
      after: sA ? (cA / sA) * 100 : 0,
    };
  }, [sales_before, sales_after, cogs_before, cogs_after]);

  const expensesTotals = useMemo(() => {
    const totalBefore = expense_categories.reduce((sum, r) => sum + (Number(r.before.replace(/[^0-9.-]/g, '')) || 0), 0);
    const totalAfter = expense_categories.reduce((sum, r) => sum + (Number(r.after.replace(/[^0-9.-]/g, '')) || 0), 0);
    const { change, pct } = netChange(totalBefore.toString(), totalAfter.toString());
    return { totalBefore, totalAfter, change, pct };
  }, [expense_categories]);

  const projections = useMemo(() => {
    const fallbackSalesY1 = Number(sales_after.replace(/[^0-9.-]/g, '')) || 0;
    const fallbackCogsY1 = Number(cogs_after.replace(/[^0-9.-]/g, '')) || 0;
    const fallbackExpY1 = expense_categories.reduce((sum, r) => sum + (Number(r.after.replace(/[^0-9.-]/g, '')) || 0), 0);

    const years = [1, 2, 3, 4, 5].map((y) => {
      const idx = y - 1;
      const salesStr = yearly_sales_after[idx] || '';
      const cogsStr = yearly_cogs_after[idx] || '';
      const expStr = yearly_expenses_after[idx] || '';
      const sales = (Number(salesStr.replace(/[^0-9.-]/g, '')) || (idx === 0 ? fallbackSalesY1 : fallbackSalesY1));
      const cogs = (Number(cogsStr.replace(/[^0-9.-]/g, '')) || (idx === 0 ? fallbackCogsY1 : fallbackCogsY1));
      const expenses = (Number(expStr.replace(/[^0-9.-]/g, '')) || (idx === 0 ? fallbackExpY1 : fallbackExpY1));
      const gross = sales - cogs;
      const net = gross - expenses;
      return { year: y, sales, cogs, gross, expenses, net };
    });
    return years;
  }, [yearly_sales_after, yearly_cogs_after, yearly_expenses_after, sales_after, cogs_after, expense_categories]);

  const addExpenseRow = () => setExpenseRows((rows) => [...rows, { id: crypto.randomUUID(), name: 'New Expense', before: '', after: '' }]);
  const removeExpenseRow = (id: string) => setExpenseRows((rows) => rows.filter((r) => r.id !== id));
  const updateExpenseRow = (id: string, field: keyof ExpenseRow, value: string) => setExpenseRows((rows) => rows.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const SectionCard: React.FC<{ title: string; children: React.ReactNode; subtitle?: string }> = ({ title, children, subtitle }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  );

  const NumberField: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder?: string }> = ({ label, value, onChange, placeholder }) => (
    <label className="flex flex-col gap-1">
      <span className="text-sm text-gray-600">{label}</span>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
    </label>
  );

  const PreviewSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 mb-4 border-b border-gray-200 pb-2">{title}</h2>
      {children}
    </div>
  );

  const PreviewTable: React.FC<{ 
    headers: string[]; 
    rows: Array<{ label: string; values: string[] }> 
  }> = ({ headers, rows }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full border border-gray-300">
        <thead>
          <tr className="bg-gray-50">
            {headers.map((header, idx) => (
              <th key={idx} className={`px-4 py-3 text-sm font-semibold text-gray-700 border-b border-gray-300 ${idx === 0 ? 'text-left' : 'text-center'}`}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
              <td className="px-4 py-3 text-sm font-medium text-gray-900 border-b border-gray-200 text-left">
                {row.label}
              </td>
              {row.values.map((value, colIdx) => (
                <td key={colIdx} className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200 text-center">
                  {value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const YearlyPlan: React.FC<{
    title: string;
    baselineLabel: string;
    baselineValue: string | number;
    yearlyValues: string[];
    setYearlyValues: (v: string[]) => void;
    yearlyNarratives: string[];
    setYearlyNarratives: (v: string[]) => void;
  }> = ({ title, baselineLabel, baselineValue, yearlyValues, setYearlyValues, yearlyNarratives, setYearlyNarratives }) => {
    const baselineNum = typeof baselineValue === 'number' ? baselineValue : Number((baselineValue as string).replace(/[^0-9.-]/g, '')) || 0;
    return (
      <div className="mt-4">
        <div className="text-sm font-medium text-gray-900 mb-2">{title}</div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="text-left text-sm text-gray-600">
                <th className="py-2 pr-4">Year</th>
                <th className="py-2 pr-4">After (Annual)</th>
                <th className="py-2 pr-4">Net Change vs {baselineLabel}</th>
                <th className="py-2 pr-4">Narrative</th>
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4, 5].map((y) => {
                const idx = y - 1;
                const afterStr = yearlyValues[idx] || '';
                const afterNum = Number(afterStr.replace(/[^0-9.-]/g, '')) || 0;
                const change = afterNum - baselineNum;
                const pct = baselineNum !== 0 ? (change / baselineNum) * 100 : 0;
                return (
                  <tr key={y} className="border-t border-gray-100 align-top">
                    <td className="py-2 pr-4">Year {y}</td>
                    <td className="py-2 pr-4 w-48">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={afterStr}
                        onChange={(e) => {
                          const next = [...yearlyValues];
                          next[idx] = e.target.value;
                          setYearlyValues(next);
                        }}
                        className="px-3 py-2 rounded-lg border border-gray-200 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="$"
                      />
                    </td>
                    <td className="py-2 pr-4 w-56">
                      <div className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                        {currency(change.toString())} ({pct.toFixed(1)}%)
                      </div>
                    </td>
                    <td className="py-2 pr-4">
                      <textarea
                        value={yearlyNarratives[idx] || ''}
                        onChange={(e) => {
                          const next = [...yearlyNarratives];
                          next[idx] = e.target.value;
                          setYearlyNarratives(next);
                        }}
                        rows={2}
                        className="px-3 py-2 rounded-lg border border-gray-200 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={`Year ${y} plan...`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPreview = () => (
    <div className="max-w-4xl mx-auto bg-white p-8 shadow-lg">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Business Acquisition Proforma</h1>
        <p className="text-lg text-gray-600">Deal Scenario: {dealId}</p>
        <p className="text-sm text-gray-500 mt-2">Generated on {new Date().toLocaleDateString()}</p>
      </div>

      {/* Executive Summary */}
      <PreviewSection title="Executive Summary">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-900">
              {(() => {
                const salesValues = [
                  Number(sales_before.replace(/[^0-9.-]/g, '')) || 0,
                  Number((yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 0,
                  Number((yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 0,
                  Number((yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 0,
                  Number((yearly_sales_after[3] || yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 0,
                  Number((yearly_sales_after[4] || yearly_sales_after[3] || yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 0
                ];
                const changes = salesValues.slice(1).map((val, idx) => salesValues[idx] ? ((val - salesValues[idx]) / salesValues[idx] * 100) : 0);
                const avgChange = changes.length > 0 ? changes.reduce((sum, change) => sum + change, 0) / changes.length : 0;
                return `${avgChange >= 0 ? '+' : ''}${avgChange.toFixed(1)}%`;
              })()}
            </div>
            <div className="text-sm text-blue-700 mb-2">5-Year Avg Sales YoY Δ</div>
            <div className="text-xs text-blue-600 flex justify-center gap-3 flex-wrap">
              {['Y1', 'Y2', 'Y3', 'Y4', 'Y5'].map((label, idx) => {
                const salesValues = [
                  Number((yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 0,
                  Number((yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 0,
                  Number((yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 0,
                  Number((yearly_sales_after[3] || yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 0,
                  Number((yearly_sales_after[4] || yearly_sales_after[3] || yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 0
                ];
                const currentVal = salesValues[idx];
                const prevVal = idx > 0 ? salesValues[idx - 1] : Number(sales_before.replace(/[^0-9.-]/g, '')) || 0;
                const change = prevVal ? ((currentVal - prevVal) / prevVal * 100) : 0;
                return (
                  <div key={idx} className="text-center">
                    <div className="font-medium">{label}</div>
                    <div>
                      {idx === 0 ? `${currency(currentVal.toString())}` : `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-900">
              {(() => {
                const netValues = projections.map(p => p.net);
                const changes = netValues.slice(1).map((val, idx) => netValues[idx] ? ((val - netValues[idx]) / netValues[idx] * 100) : 0);
                const avgChange = changes.length > 0 ? changes.reduce((sum, change) => sum + change, 0) / changes.length : 0;
                return `${avgChange >= 0 ? '+' : ''}${avgChange.toFixed(1)}%`;
              })()}
            </div>
            <div className="text-sm text-green-700 mb-2">5-Year Avg Net Income YoY Δ</div>
            <div className="text-xs text-green-600 flex justify-center gap-3 flex-wrap">
              {projections.map((p, idx) => {
                const currentNet = p.net;
                const prevNet = idx > 0 ? projections[idx - 1].net : 0;
                const change = idx > 0 && prevNet ? ((currentNet - prevNet) / prevNet * 100) : 0;
                return (
                  <div key={idx} className="text-center">
                    <div className="font-medium">Y{idx + 1}</div>
                    <div>
                      {idx === 0 ? `${currency(currentNet.toString())}` : `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-900">
              {(() => {
                const margins = projections.map(p => p.sales ? (p.net / p.sales * 100) : 0);
                const marginChanges = margins.slice(1).map((margin, idx) => margin - margins[idx]);
                const avgChange = marginChanges.length > 0 ? marginChanges.reduce((sum, change) => sum + change, 0) / marginChanges.length : 0;
                return `${avgChange >= 0 ? '+' : ''}${avgChange.toFixed(1)}%`;
              })()}
            </div>
            <div className="text-sm text-purple-700 mb-2">5-Year Avg Margin YoY Δ</div>
            <div className="text-xs text-purple-600 flex justify-center gap-3 flex-wrap">
              {projections.map((p, idx) => {
                const currentMargin = p.sales ? (p.net / p.sales * 100) : 0;
                const prevMargin = idx > 0 && projections[idx - 1]?.sales ? (projections[idx - 1].net / projections[idx - 1].sales * 100) : 0;
                const change = idx > 0 ? currentMargin - prevMargin : 0;
                return (
                  <div key={idx} className="text-center">
                    <div className="font-medium">Y{idx + 1}</div>
                    <div>
                      {idx === 0 ? `${currentMargin.toFixed(1)}%` : `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </PreviewSection>

      {/* Sales Analysis */}
      <PreviewSection title="Sales Strategy & Projections">
        {sales_narrative && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Strategy</h3>
            <p className="text-gray-700 text-sm leading-relaxed">{sales_narrative}</p>
          </div>
        )}
        <PreviewTable
          headers={['Metric', baseline_year, 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5']}
          rows={[
            {
              label: 'Annual Sales',
              values: [
                currency(sales_before),
                currency(yearly_sales_after[0] || sales_after || '0'),
                currency(yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0'),
                currency(yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0'),
                currency(yearly_sales_after[3] || yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0'),
                currency(yearly_sales_after[4] || yearly_sales_after[3] || yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0')
              ]
            }
          ]}
        />
      </PreviewSection>

      {/* COGS Analysis */}
      <PreviewSection title="Cost of Goods Sold Analysis">
        {cogs_narrative && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Strategy</h3>
            <p className="text-gray-700 text-sm leading-relaxed">{cogs_narrative}</p>
          </div>
        )}
        <PreviewTable
          headers={['Metric', baseline_year, 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5']}
          rows={[
            {
              label: 'Annual COGS',
              values: [
                currency(cogs_before),
                currency(yearly_cogs_after[0] || cogs_after || '0'),
                currency(yearly_cogs_after[1] || yearly_cogs_after[0] || cogs_after || '0'),
                currency(yearly_cogs_after[2] || yearly_cogs_after[1] || yearly_cogs_after[0] || cogs_after || '0'),
                currency(yearly_cogs_after[3] || yearly_cogs_after[2] || yearly_cogs_after[1] || yearly_cogs_after[0] || cogs_after || '0'),
                currency(yearly_cogs_after[4] || yearly_cogs_after[3] || yearly_cogs_after[2] || yearly_cogs_after[1] || yearly_cogs_after[0] || cogs_after || '0')
              ]
            },
            {
              label: 'COGS as % of Sales',
              values: [
                `${cogsPctOfSales.before.toFixed(1)}%`,
                `${((Number((yearly_cogs_after[0] || cogs_after || '0').replace(/[^0-9.-]/g, '')) || 0) / (Number((yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 1) * 100).toFixed(1)}%`,
                `${((Number((yearly_cogs_after[1] || yearly_cogs_after[0] || cogs_after || '0').replace(/[^0-9.-]/g, '')) || 0) / (Number((yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 1) * 100).toFixed(1)}%`,
                `${((Number((yearly_cogs_after[2] || yearly_cogs_after[1] || yearly_cogs_after[0] || cogs_after || '0').replace(/[^0-9.-]/g, '')) || 0) / (Number((yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 1) * 100).toFixed(1)}%`,
                `${((Number((yearly_cogs_after[3] || yearly_cogs_after[2] || yearly_cogs_after[1] || yearly_cogs_after[0] || cogs_after || '0').replace(/[^0-9.-]/g, '')) || 0) / (Number((yearly_sales_after[3] || yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 1) * 100).toFixed(1)}%`,
                `${((Number((yearly_cogs_after[4] || yearly_cogs_after[3] || yearly_cogs_after[2] || yearly_cogs_after[1] || yearly_cogs_after[0] || cogs_after || '0').replace(/[^0-9.-]/g, '')) || 0) / (Number((yearly_sales_after[4] || yearly_sales_after[3] || yearly_sales_after[2] || yearly_sales_after[1] || yearly_sales_after[0] || sales_after || '0').replace(/[^0-9.-]/g, '')) || 1) * 100).toFixed(1)}%`
              ]
            }
          ]}
        />
      </PreviewSection>

      {/* Expenses Analysis */}
      <PreviewSection title="Operating Expenses Analysis">
        {expenses_narrative && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Strategy</h3>
            <p className="text-gray-700 text-sm leading-relaxed">{expenses_narrative}</p>
          </div>
        )}
        <PreviewTable
          headers={['Metric', baseline_year, 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5']}
          rows={[
            {
              label: 'Total Operating Expenses',
              values: [
                currency(expenses_before),
                currency(yearly_expenses_after[0] || expenses_after || '0'),
                currency(yearly_expenses_after[1] || yearly_expenses_after[0] || expenses_after || '0'),
                currency(yearly_expenses_after[2] || yearly_expenses_after[1] || yearly_expenses_after[0] || expenses_after || '0'),
                currency(yearly_expenses_after[3] || yearly_expenses_after[2] || yearly_expenses_after[1] || yearly_expenses_after[0] || expenses_after || '0'),
                currency(yearly_expenses_after[4] || yearly_expenses_after[3] || yearly_expenses_after[2] || yearly_expenses_after[1] || yearly_expenses_after[0] || expenses_after || '0')
              ]
            }
          ]}
        />
      </PreviewSection>

      {/* 5-Year Financial Projections */}
      <PreviewSection title="5-Year Financial Projections">
        <PreviewTable
          headers={['', 'Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5']}
          rows={[
            { label: 'Sales', values: projections.map(p => currency(p.sales.toFixed(0))) },
            { label: 'COGS', values: projections.map(p => currency(p.cogs.toFixed(0))) },
            { label: 'Gross Profit', values: projections.map(p => currency(p.gross.toFixed(0))) },
            { label: 'Operating Expenses', values: projections.map(p => currency(p.expenses.toFixed(0))) },
            { label: 'Net Income', values: projections.map(p => currency(p.net.toFixed(0))) }
          ]}
        />
      </PreviewSection>

      {/* Key Assumptions */}
      <PreviewSection title="Key Assumptions & Notes">
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Sales Growth Plans (Years 1-5)</h3>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="text-sm text-gray-700 leading-relaxed">
                {yearly_sales_narratives.filter(n => n).length > 0 ? (
                  yearly_sales_narratives.map((narrative, idx) => 
                    narrative ? (
                      <div key={idx} className="mb-3 last:mb-0">
                        <span className="font-medium text-blue-900">Year {idx + 1}:</span> {narrative}
                      </div>
                    ) : null
                  )
                ) : (
                  <span className="text-gray-500 italic">No specific sales growth plans documented.</span>
                )}
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Cost Optimization Plans (Years 1-5)</h3>
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="text-sm text-gray-700 leading-relaxed">
                {yearly_cogs_narratives.filter(n => n).length > 0 ? (
                  yearly_cogs_narratives.map((narrative, idx) => 
                    narrative ? (
                      <div key={idx} className="mb-3 last:mb-0">
                        <span className="font-medium text-green-900">Year {idx + 1}:</span> {narrative}
                      </div>
                    ) : null
                  )
                ) : (
                  <span className="text-gray-500 italic">No specific cost optimization plans documented.</span>
                )}
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Expense Management Plans (Years 1-5)</h3>
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="text-sm text-gray-700 leading-relaxed">
                {yearly_expenses_narratives.filter(n => n).length > 0 ? (
                  yearly_expenses_narratives.map((narrative, idx) => 
                    narrative ? (
                      <div key={idx} className="mb-3 last:mb-0">
                        <span className="font-medium text-purple-900">Year {idx + 1}:</span> {narrative}
                      </div>
                    ) : null
                  )
                ) : (
                  <span className="text-gray-500 italic">No specific expense management plans documented.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </PreviewSection>
    </div>
  );

  // Check if user has read-only access - show limited proforma access
  const isReadOnlyAccess = selectedDeal?.access_type === 'read';

  return (
    <div className="py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Proforma</h1>
          <p className="text-gray-600">Deal Scenario: {dealId}</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Only show save button for write/admin users */}
          {!isReadOnlyAccess && (
            <button 
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          )}
          <button 
            onClick={() => !isReadOnlyAccess && setIsPreviewMode(!isPreviewMode)}
            disabled={isReadOnlyAccess}
            className={`px-4 py-2 rounded-lg ${isPreviewMode ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'} ${isReadOnlyAccess ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isPreviewMode ? 'Edit' : 'Preview'}
          </button>
          <button className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200">Export PDF</button>
          <button className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200">Print View</button>
        </div>
      </div>

      {/* Read-only access notice */}
      {isReadOnlyAccess && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            <div>
              <h4 className="text-sm font-medium text-blue-900">Read-Only Access</h4>
              <p className="text-sm text-blue-800">
                You have read-only access to this proforma. You can view and export but cannot make changes.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Save message and error display */}
      {saveMessage && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          {saveMessage}
        </div>
      )}
      {saveError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {saveError}
        </div>
      )}

      {isPreviewMode ? renderPreview() : (
        <>
          {/* Baseline Year Selection */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Baseline Year</h2>
              <p className="text-sm text-gray-500 mt-1">Select the most recent tax return or TTM year as your baseline</p>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Baseline Year:</span>
                <select
                  value={baseline_year}
                  onChange={(e) => setBaselineYear(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="2024">2024</option>
                  <option value="TTM">TTM</option>
                  <option value="3yr Avg">3yr Avg</option>
                  <option value="Current Year (Proj)">Current Year (Proj)</option>
                </select>
              </label>
            </div>
          </div>

          {/* Sales Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <SectionCard title="Sales" subtitle="Describe sales strategy and expected impact">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-sm text-gray-600">Narrative</span>
                <textarea
                  value={sales_narrative}
                  onChange={(e) => setSalesNarrative(e.target.value)}
                  rows={6}
                  className="px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Free text..."
                />
              </label>
              <div>
                <button
                  onClick={() => {
                    setRefiningSales(true);
                    setTimeout(() => {
                      setSalesNarrative((t) => (t ? t + ' — refined' : 'Refined narrative'));
                      setRefiningSales(false);
                    }, 900);
                  }}
                  disabled={refiningSales}
                  className={`px-4 py-2 rounded-lg ${refiningSales ? 'bg-gray-200 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                >
                  {refiningSales ? 'Refining…' : 'Refine with AI'}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <NumberField label="Before (Annual)" value={sales_before} onChange={setSalesBefore} placeholder="$" />
              <NumberField label="After (Annual)" value={sales_after} onChange={setSalesAfter} placeholder="$" />
              {(() => {
                const { change, pct } = netChange(sales_before, sales_after);
                return (
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">Net Change</span>
                    <div className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                      <div className="text-gray-900">{currency(change.toString())} ({pct.toFixed(1)}%)</div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </SectionCard>

        <YearlyPlan
          title="Sales Yearly Plan (Years 1-5)"
          baselineLabel={`Sales ${baseline_year}`}
          baselineValue={sales_before}
          yearlyValues={yearly_sales_after}
          setYearlyValues={setYearlySalesAfter}
          yearlyNarratives={yearly_sales_narratives}
          setYearlyNarratives={setYearlySalesNarratives}
        />

        {/* COGS */}
        <SectionCard title="Cost of Goods Sold (COGS)" subtitle="Describe COGS optimization and sourcing">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-sm text-gray-600">Narrative</span>
                <textarea
                  value={cogs_narrative}
                  onChange={(e) => setCogsNarrative(e.target.value)}
                  rows={6}
                  className="px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Free text..."
                />
              </label>
              <div>
                <button
                  onClick={() => {
                    setRefiningCogs(true);
                    setTimeout(() => {
                      setCogsNarrative((t) => (t ? t + ' — refined' : 'Refined narrative'));
                      setRefiningCogs(false);
                    }, 900);
                  }}
                  disabled={refiningCogs}
                  className={`px-4 py-2 rounded-lg ${refiningCogs ? 'bg-gray-200 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                >
                  {refiningCogs ? 'Refining…' : 'Refine with AI'}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <NumberField label="Before (Annual)" value={cogs_before} onChange={setCogsBefore} placeholder="$" />
              <NumberField label="After (Annual)" value={cogs_after} onChange={setCogsAfter} placeholder="$" />
              {(() => {
                const { change, pct } = netChange(cogs_before, cogs_after);
                return (
                  <div className="flex flex-col gap-1">
                    <span className="text-sm text-gray-600">Net Change</span>
                    <div className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                      <div className="text-gray-900">{currency(change.toString())} ({pct.toFixed(1)}%)</div>
                    </div>
                  </div>
                );
              })()}
              <div className="flex flex-col gap-1">
                <span className="text-sm text-gray-600">COGS as % of Sales</span>
                <div className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="text-gray-900">Before {cogsPctOfSales.before.toFixed(1)}% → After {cogsPctOfSales.after.toFixed(1)}%</div>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>
        <YearlyPlan
          title="COGS Yearly Plan (Years 1-5)"
          baselineLabel={`COGS ${baseline_year}`}
          baselineValue={cogs_before}
          yearlyValues={yearly_cogs_after}
          setYearlyValues={setYearlyCogsAfter}
          yearlyNarratives={yearly_cogs_narratives}
          setYearlyNarratives={setYearlyCogsNarratives}
        />
      </div>

      {/* Expenses */}
      <SectionCard title="Expenses" subtitle="Customize categories and amounts">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-sm text-gray-600">Narrative</span>
              <textarea
                value={expenses_narrative}
                onChange={(e) => setExpensesNarrative(e.target.value)}
                rows={4}
                className="px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Free text..."
              />
            </label>
            <div>
              <button
                onClick={() => {
                  setRefiningExpenses(true);
                  setTimeout(() => {
                    setExpensesNarrative((t) => (t ? t + ' — refined' : 'Refined narrative'));
                    setRefiningExpenses(false);
                  }, 900);
                }}
                disabled={refiningExpenses}
                className={`px-4 py-2 rounded-lg ${refiningExpenses ? 'bg-gray-200 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
              >
                {refiningExpenses ? 'Refining…' : 'Refine with AI'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-left text-sm text-gray-600">
                  <th className="py-2 pr-4">Expense Name</th>
                  <th className="py-2 pr-4">Before (Annual)</th>
                  <th className="py-2 pr-4">After (Annual)</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {expense_categories.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100">
                    <td className="py-2 pr-4">
                      <input
                        type="text"
                        value={row.name}
                        onChange={(e) => updateExpenseRow(row.id, 'name', e.target.value)}
                        className="px-3 py-2 rounded-lg border border-gray-200 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={row.before}
                        onChange={(e) => updateExpenseRow(row.id, 'before', e.target.value)}
                        className="px-3 py-2 rounded-lg border border-gray-200 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="$"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={row.after}
                        onChange={(e) => updateExpenseRow(row.id, 'after', e.target.value)}
                        className="px-3 py-2 rounded-lg border border-gray-200 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="$"
                      />
                    </td>
                    <td className="py-2 pr-4">
                      <button
                        onClick={() => removeExpenseRow(row.id)}
                        className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <button onClick={addExpenseRow} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">Add Row</button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <NumberField label="Total Before (Annual)" value={expenses_before} onChange={setExpensesBefore} placeholder="$" />
            <NumberField label="Total After (Annual)" value={expenses_after} onChange={setExpensesAfter} placeholder="$" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col gap-1">
              <span className="text-sm text-gray-600">Calculated Before</span>
              <div className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">{currency(expensesTotals.totalBefore.toString())}</div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-gray-600">Calculated After</span>
              <div className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">{currency(expensesTotals.totalAfter.toString())}</div>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm text-gray-600">Net Change</span>
              <div className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">{currency(expensesTotals.change.toString())} ({expensesTotals.pct.toFixed(1)}%)</div>
            </div>
          </div>
        </div>
      </SectionCard>

      <YearlyPlan
        title="Expenses Yearly Plan (Years 1-5)"
        baselineLabel={`Total Expenses ${baseline_year}`}
        baselineValue={expenses_before}
        yearlyValues={yearly_expenses_after}
        setYearlyValues={setYearlyExpensesAfter}
        yearlyNarratives={yearly_expenses_narratives}
        setYearlyNarratives={setYearlyExpensesNarratives}
      />

      {/* Timeline */}
      <SectionCard title="Timeline" subtitle="Auto-populated from changes above">
        <div className="text-sm text-gray-600">This is a visual placeholder. Timeline items will reflect changes from the sections above.</div>
      </SectionCard>

      {/* Financial Projections */}
      <SectionCard title="Financial Projections" subtitle="5-Year Summary (uses values from yearly plans)">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="text-left text-sm text-gray-600">
                <th className="py-2 pr-6">&nbsp;</th>
                {projections.map((p) => (
                  <th key={p.year} className="py-2 pr-6">Year {p.year}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gray-100">
                <td className="py-2 pr-6">Sales</td>
                {projections.map((p) => (
                  <td key={`s-${p.year}`} className="py-2 pr-6">{currency(p.sales.toFixed(0))}</td>
                ))}
              </tr>
              <tr className="border-t border-gray-100">
                <td className="py-2 pr-6">COGS</td>
                {projections.map((p) => (
                  <td key={`c-${p.year}`} className="py-2 pr-6">{currency(p.cogs.toFixed(0))}</td>
                ))}
              </tr>
              <tr className="border-t border-gray-100">
                <td className="py-2 pr-6">Gross Profit</td>
                {projections.map((p) => (
                  <td key={`g-${p.year}`} className="py-2 pr-6">{currency(p.gross.toFixed(0))}</td>
                ))}
              </tr>
              <tr className="border-t border-gray-100">
                <td className="py-2 pr-6">Expenses</td>
                {projections.map((p) => (
                  <td key={`e-${p.year}`} className="py-2 pr-6">{currency(p.expenses.toFixed(0))}</td>
                ))}
              </tr>
              <tr className="border-t border-gray-100">
                <td className="py-2 pr-6 font-medium">Net Income</td>
                {projections.map((p) => (
                  <td key={`n-${p.year}`} className="py-2 pr-6 font-medium">{currency(p.net.toFixed(0))}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </SectionCard>
        </>
      )}
    </div>
  );
};

export default ProformaPage;


